/* eslint-disable */

export const THEME_STORAGE_KEY = "app.theme";
export const DEFAULT_THEME_ID = "equipped";

/* ---------------------------------------------------------------
   LIGHT / DARK / SYSTEM MODE
   Independent of the color theme. Stored separately and applied as
   document.documentElement[data-mode="light" | "dark"]. "system"
   is stored as-is but always resolved to light/dark before being
   written to the DOM, because the dark overlay in main.scss only
   matches data-mode="dark". A matchMedia listener re-resolves it
   live when the OS preference changes.
   NOTE: the pre-paint bootstrap in index.html mirrors the storage
   keys and the system-resolution logic — keep them in sync.
   ---------------------------------------------------------------*/
export const MODE_STORAGE_KEY = "app.mode";
export const MODES = { LIGHT: "light", DARK: "dark", SYSTEM: "system" };
export const DEFAULT_MODE = MODES.LIGHT;

export const MODIFIER_KEY =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || "")
    ? "⌘"
    : "Ctrl";

export const STRINGS = {
  HERO_TITLE: "Choose your workspace theme",
  HERO_SUBTITLE:
    "Switch the full app palette instantly. Your choice applies everywhere and is saved for next time.",
  SEARCH_PLACEHOLDER: "Search themes…",
  RADIOGROUP_LABEL: "Workspace theme",
  ACTIVE_LABEL: "Active",
  MODE_LABELS: {
    [MODES.LIGHT]: "Light mode",
    [MODES.DARK]: "Dark mode",
    [MODES.SYSTEM]: "System",
  },
  SHORTCUTS: {
    SWITCH_THEME: "Switch theme",
    TOGGLE_MODE: "Toggle mode",
  },
  KEYS: {
    PREV: "←",
    NEXT: "→",
    MODE: "M",
  },
  noMatchMessage: (query) => `No themes match “${query}”.`,
  modeSwitchAria: (nextLabel) => `Switch appearance to ${nextLabel}`,
};

export const isValidMode = (mode) =>
  mode === MODES.LIGHT || mode === MODES.DARK || mode === MODES.SYSTEM;

const getSystemMode = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-color-scheme: dark)").matches
    ? MODES.DARK
    : MODES.LIGHT;

export const resolveMode = (mode) => {
  if (mode === MODES.SYSTEM) {
    return getSystemMode();
  }
  return isValidMode(mode) ? mode : DEFAULT_MODE;
};

export const getResolvedMode = () => resolveMode(getStoredMode());

// Global lock to prevent overlapping view transitions during rapid switching
let isTransitioning = false;

const setWipeOrigin = (coords) => {
  const root = document.documentElement;
  if (coords && coords.x !== undefined) {
    root.style.setProperty("--wipe-x", `${coords.x}px`);
    root.style.setProperty("--wipe-y", `${coords.y}px`);
  } else {
    root.style.setProperty("--wipe-x", "0%");
    root.style.setProperty("--wipe-y", "100%");
  }
};

const runWithViewTransition = (performChange, skipAnimation) => {
  if (
    !skipAnimation &&
    !isTransitioning &&
    typeof document !== "undefined" &&
    document.startViewTransition &&
    typeof window !== "undefined" &&
    !window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ) {
    isTransitioning = true;
    const transition = document.startViewTransition(() => performChange());
    transition.finished.finally(() => {
      isTransitioning = false;
    });
  } else {
    performChange();
  }
};

export const applyMode = (mode, coords = null, skipAnimation = false) => {
  const next = isValidMode(mode) ? mode : DEFAULT_MODE;

  const performModeChange = () => {
    if (typeof document !== "undefined" && document.documentElement) {
      setWipeOrigin(coords);
      document.documentElement.setAttribute("data-mode", resolveMode(next));
    }
    try {
      localStorage.setItem(MODE_STORAGE_KEY, next);
    } catch (error) {
      // localStorage may be unavailable; fail silently
    }
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("themeChange", { detail: { mode: next } })
      );
    }
  };

  runWithViewTransition(performModeChange, skipAnimation);
  return next;
};

export const getStoredMode = () => {
  try {
    const value = localStorage.getItem(MODE_STORAGE_KEY);
    return isValidMode(value) ? value : DEFAULT_MODE;
  } catch (error) {
    return DEFAULT_MODE;
  }
};

export const toggleMode = (coords = null) =>
  applyMode(
    getResolvedMode() === MODES.DARK ? MODES.LIGHT : MODES.DARK,
    coords
  );

const MODE_CYCLE = [MODES.LIGHT, MODES.DARK, MODES.SYSTEM];

export const getNextMode = (mode) => {
  const current = isValidMode(mode) ? mode : DEFAULT_MODE;
  return MODE_CYCLE[(MODE_CYCLE.indexOf(current) + 1) % MODE_CYCLE.length];
};

export const cycleMode = (coords = null) =>
  applyMode(getNextMode(getStoredMode()), coords);

let systemListenerAttached = false;

const watchSystemMode = () => {
  if (
    systemListenerAttached ||
    typeof window === "undefined" ||
    !window.matchMedia
  ) {
    return;
  }
  systemListenerAttached = true;
  const media = window.matchMedia("(prefers-color-scheme: dark)");
  const handleChange = () => {
    if (getStoredMode() === MODES.SYSTEM) {
      applyMode(MODES.SYSTEM, null, true);
    }
  };
  if (media.addEventListener) {
    media.addEventListener("change", handleChange);
  } else if (media.addListener) {
    media.addListener(handleChange);
  }
};

export const initMode = () => {
  applyMode(getStoredMode(), null, true);
  watchSystemMode();
};

export const SIDEBAR_VIBRANT_STORAGE_KEY = "app.sidebarVibrant";
export const DEFAULT_SIDEBAR_VIBRANT = false;

export const getStoredSidebarVibrant = () => {
  try {
    return localStorage.getItem(SIDEBAR_VIBRANT_STORAGE_KEY) === "true";
  } catch (error) {
    return DEFAULT_SIDEBAR_VIBRANT;
  }
};

export const applySidebarVibrant = (enabled) => {
  const next = Boolean(enabled);
  try {
    localStorage.setItem(SIDEBAR_VIBRANT_STORAGE_KEY, String(next));
  } catch (error) {
    // localStorage may be unavailable; fail silently
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("themeChange", { detail: { sidebarVibrant: next } })
    );
  }
  return next;
};

export const SIDEBAR_OPEN_STORAGE_KEY = "sidebarOpen";
export const DEFAULT_SIDEBAR_OPEN = true;

export const getStoredSidebarOpen = () => {
  try {
    const raw = localStorage.getItem(SIDEBAR_OPEN_STORAGE_KEY);
    return raw !== null ? JSON.parse(raw) === true : DEFAULT_SIDEBAR_OPEN;
  } catch (error) {
    return DEFAULT_SIDEBAR_OPEN;
  }
};

export const applySidebarOpen = (open) => {
  const next = Boolean(open);
  try {
    localStorage.setItem(SIDEBAR_OPEN_STORAGE_KEY, JSON.stringify(next));
  } catch (error) {
    // localStorage may be unavailable; fail silently
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("themeChange", { detail: { sidebarOpen: next } })
    );
  }
  return next;
};

export const THEME_OPTIONS = [
  { value: "equipped", label: "Equipped", group: "Blues, Teals & Greens" },
  { value: "sapphire", label: "Sapphire", group: "Blues, Teals & Greens" },
  // { value: "forest-mist", label: "Forest Mist", group: "Blues, Teals & Greens" },
  // { value: "nordic-light", label: "Nordic Light", group: "Blues, Teals & Greens" },
  // { value: "deep-petrol", label: "Deep Petrol", group: "Blues, Teals & Greens" },
  { value: "htb", label: "HTB", group: "Brand" },
  { value: "equipped-cloud", label: "Equipped Cloud", group: "Brand" },
  // { value: "executive-blue", label: "Executive Blue", group: "Brand" },
  // { value: "indigo", label: "Indigo", group: "Purples & Pinks" },
  // { value: "midnight-violet", label: "Midnight Violet", group: "Purples & Pinks" },
  // { value: "blush-quartz", label: "Blush Quartz", group: "Purples & Pinks" },
  { value: "rose-gold", label: "Rose Gold", group: "Purples & Pinks" },
  { value: "bordeaux", label: "Bordeaux", group: "Reds" },
  // { value: "crimson", label: "Crimson", group: "Reds" },
  // { value: "amber", label: "Amber", group: "Warm Neutrals" },
  { value: "copper-dusk", label: "Copper Dusk", group: "Warm Neutrals" },
  { value: "matcha", label: "Matcha", group: "Earth Tones" },
  { value: "slate", label: "Slate", group: "Dark Neutrals" },
  { value: "sterling", label: "Sterling", group: "Dark Neutrals" },
  // { value: "obsidian", label: "Obsidian", group: "Dark Neutrals" },
];

export const groupThemes = (themes) => {
  const groups = [];
  const indexByName = {};
  themes.forEach((theme) => {
    const name = theme.group || "Other";
    if (indexByName[name] === undefined) {
      indexByName[name] = groups.length;
      groups.push({ group: name, themes: [] });
    }
    groups[indexByName[name]].themes.push(theme);
  });
  return groups;
};

export const isValidTheme = (themeId) =>
  Boolean(themeId) && THEME_OPTIONS.some((option) => option.value === themeId);

export const applyTheme = (themeId, coords = null, skipAnimation = false) => {
  if (typeof document === "undefined" || !document.documentElement) {
    return;
  }
  const id = isValidTheme(themeId) ? themeId : DEFAULT_THEME_ID;

  const performThemeChange = () => {
    setWipeOrigin(coords);
    document.documentElement.setAttribute("data-theme", id);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, id);
    } catch (error) {
      // localStorage may be unavailable; fail silently
    }
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("themeChange", { detail: { theme: id } })
      );
    }
  };

  runWithViewTransition(performThemeChange, skipAnimation);
};

export const getStoredTheme = () => {
  try {
    const value = localStorage.getItem(THEME_STORAGE_KEY);
    return isValidTheme(value) ? value : DEFAULT_THEME_ID;
  } catch (error) {
    return DEFAULT_THEME_ID;
  }
};

let storageListenerAttached = false;

const watchOtherTabs = () => {
  if (storageListenerAttached || typeof window === "undefined") {
    return;
  }
  storageListenerAttached = true;
  window.addEventListener("storage", (event) => {
    if (event.key === THEME_STORAGE_KEY && isValidTheme(event.newValue)) {
      applyTheme(event.newValue, null, true);
    }
    if (event.key === MODE_STORAGE_KEY && isValidMode(event.newValue)) {
      applyMode(event.newValue, null, true);
    }
  });
};

export const initTheme = () => {
  applyTheme(getStoredTheme(), null, true);
  watchOtherTabs();
};

/* ---------------------------------------------------------------
   GLOBAL KEYBOARD SHORTCUTS
   Ctrl/Cmd + left/right steps through THEME_OPTIONS and Ctrl/Cmd + M
   cycles light/dark/system. Attached once at app start (see
   src/index.js) rather than from the theme panel, so the shortcuts it
   advertises keep working with the panel closed. applyTheme/applyMode
   broadcast a "themeChange" event, so an open panel re-syncs itself.
   ---------------------------------------------------------------*/

export const stepTheme = (step) => {
  if (THEME_OPTIONS.length === 0) {
    return null;
  }
  // Storage is the source of truth here: the panel that owns the React
  // state may not be mounted at all.
  const current = THEME_OPTIONS.findIndex(
    (option) => option.value === getStoredTheme()
  );
  const base = current === -1 ? 0 : current;
  const size = THEME_OPTIONS.length;
  const next = THEME_OPTIONS[(base + step + size) % size];
  applyTheme(next.value);
  return next.value;
};

const isTextEntryTarget = (target) =>
  Boolean(target) &&
  (target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.isContentEditable === true);

let shortcutListenerAttached = false;

export const initThemeShortcuts = () => {
  if (shortcutListenerAttached || typeof window === "undefined") {
    return;
  }
  shortcutListenerAttached = true;

  window.addEventListener("keydown", (event) => {
    if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey) {
      return;
    }

    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      // Ctrl + arrow already means "move by word" in a text field
      if (isTextEntryTarget(event.target)) {
        return;
      }
      event.preventDefault();
      stepTheme(event.key === "ArrowRight" ? 1 : -1);
      return;
    }

    if (event.key === "m" || event.key === "M") {
      event.preventDefault();
      cycleMode();
    }
  });
};
